import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  createStripeAccountForOrganization,
  getOrganizationStripeStatus,
  getStripeAccountLink,
  getStripeDashboardLoginLink,
  StripeConnectStatusState,
} from "@/services/stripe-connect.service";

type ActionType = "connect" | "continue" | "dashboard" | "refresh" | null;

interface UseStripeConnectSettingsResult {
  status: StripeConnectStatusState | null;
  loading: boolean;
  error: string | null;
  action: ActionType;
  refresh: () => Promise<void>;
  handleConnect: () => Promise<void>;
  handleContinueOnboarding: () => Promise<void>;
  handleOpenDashboard: () => Promise<void>;
  formattedRequirements: string[];
}

const OPEN_WINDOW_FEATURES = "noopener,noreferrer";

const shownFromStoredStatus = (status: StripeConnectStatusState): boolean => status.status !== "no_account" && !status.checkedWithStripe;

export function useStripeConnectSettings(): UseStripeConnectSettingsResult {
  const [status, setStatus] = useState<StripeConnectStatusState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<ActionType>(null);

  const fetchStatus = useCallback(async (): Promise<StripeConnectStatusState | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await getOrganizationStripeStatus();
      setStatus(result);
      return result;
    } catch (err: any) {
      const message = err?.message ?? "Unable to load Stripe status.";
      setError(message);
      toast.error(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  // Stripe redirects expired/used onboarding links to the refresh URL, which
  // is this page with ?stripe_refresh=1. Mint a fresh link and send the user
  // straight back into onboarding instead of leaving them on the status page.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("stripe_refresh") !== "1") return;

    url.searchParams.delete("stripe_refresh");
    window.history.replaceState(window.history.state, "", url.toString());

    setAction("continue");
    getStripeAccountLink()
      .then((onboardingUrl) => {
        window.location.assign(onboardingUrl);
      })
      .catch((err: any) => {
        const message = err?.message ?? "Failed to resume Stripe onboarding.";
        setError(message);
        toast.error(message);
        setAction((current) => (current === "continue" ? null : current));
      });
  }, []);

  const refresh = useCallback(async () => {
    setAction("refresh");
    try {
      const refreshed = await fetchStatus();
      if (refreshed === null) return;
      if (shownFromStoredStatus(refreshed)) toast.info("Stripe could not be checked right now, so the last stored status is shown.");
      else toast.success("Stripe status refreshed.");
    } finally {
      setAction((current) => (current === "refresh" ? null : current));
    }
  }, [fetchStatus]);

  const handleConnect = useCallback(async () => {
    setAction("connect");
    setError(null);
    try {
      await createStripeAccountForOrganization();
      toast.success("Stripe account created. Opening onboarding…");

      const onboardingUrl = await getStripeAccountLink();
      if (typeof window !== "undefined") {
        window.open(onboardingUrl, "_blank", OPEN_WINDOW_FEATURES);
      }

      await fetchStatus();
    } catch (err: any) {
      const message = err?.message ?? "Failed to create Stripe account.";
      setError(message);
      toast.error(message);
    } finally {
      setAction((current) => (current === "connect" ? null : current));
    }
  }, [fetchStatus]);

  const handleContinueOnboarding = useCallback(async () => {
    if (!status || status.status === "no_account") {
      toast.error("Stripe account not found.");
      return;
    }

    setAction("continue");
    setError(null);
    const newTab = window.open("", "_blank");

    try {
      const onboardingUrl = await getStripeAccountLink();
      if (newTab) {
        newTab.location.href = onboardingUrl;
      }

      toast.success("Opening Stripe onboarding.");
      await fetchStatus();
    } catch (err: any) {
      const message = err?.message ?? "Failed to load onboarding link.";
      setError(message);
      toast.error(message);
    } finally {
      setAction((current) => (current === "continue" ? null : current));
    }
  }, [fetchStatus, status]);

  const handleOpenDashboard = useCallback(async () => {
    if (!status || status.status === "no_account") {
      toast.error("Stripe account not found.");
      return;
    }

    setAction("dashboard");
    setError(null);

    try {
      const loginUrl = await getStripeDashboardLoginLink();

      if (typeof window !== "undefined") {
        window.open(loginUrl, "_blank", OPEN_WINDOW_FEATURES);
      }

      toast.success("Opening Stripe dashboard.");
    } catch (err: any) {
      const message = err?.message ?? "Failed to open Stripe dashboard.";
      setError(message);
      toast.error(message);
    } finally {
      setAction((current) => (current === "dashboard" ? null : current));
    }
  }, [status]);

  const formattedRequirements = useMemo(() => {
    if (status?.status !== "pending") {
      return [];
    }

    const requirements = status.requirementsDue ?? [];
    return requirements.map((item) => item.replace(/_/g, " "));
  }, [status]);

  return {
    status,
    loading,
    error,
    action,
    refresh,
    handleConnect,
    handleContinueOnboarding,
    handleOpenDashboard,
    formattedRequirements,
  };
}
